/**
 * DocumentList — one shared, parameterized list for all six document types
 * (Invoices/Quotes/Credit Notes/Bills/Vendor Credits/Expenses), per design §2.
 * Premium pass: segmented tabs with count pills, elevated table card with
 * hover-reactive rows, gradient customer avatars, reveal-on-hover delete, and a
 * chip-style footer. Status tabs + live counts, search, draft/AI/void
 * treatments, and a per-row delete (confirm → delete → success; drafts only).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useBaseCurrency } from '../context/currencyContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import SkIcon from '../components/skIcon';
import StatusPill from '../components/statusPill';
import ConfirmDialog from '../components/confirmDialog';
import SuccessDialog from '../components/successDialog';
import { DocRow, fmtMoney, initials, money } from '../utils/format';
import { listDocuments, deleteDocument } from '../services/billing/document.service';
import { DocType } from '../schema/billing/common';

interface Props {
  docType: DocType;
  newLabel: string;
  onOpenDoc: (doc: DocRow) => void;
  onNew: () => void;
}

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

// Primary CTA color — a premium indigo→violet gradient, distinct from the
// app's accent blue so the "New" action stands out.
const CTA_A = '#6366f1';
const CTA_B = '#8b5cf6';

// Per-customer avatar gradient — gives the list a lively, CRM-grade feel while
// staying consistent for the same name.
const AV: Array<[string, string]> = [
  ['#f97316', '#db2777'],
  ['#3b82f6', '#6366f1'],
  ['#10b981', '#059669'],
  ['#f59e0b', '#ea580c'],
  ['#8b5cf6', '#6d28d9'],
  ['#06b6d4', '#0891b2'],
];
const avatarOf = (name: string): [string, string] => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % AV.length;
  return AV[h];
};

// Per-document-type glyph for the titled card header.
const DOC_ICON: Record<string, string> = {
  INVOICE: 'fileText', QUOTE: 'quote', CREDIT_NOTE: 'creditNote', BILL: 'cart', VENDOR_CREDIT: 'receipt', EXPENSE: 'wallet',
};

const TABS = [
  { id: 'all', label: 'All', f: () => true },
  { id: 'draft', label: 'Drafts', f: (d: DocRow) => d.status === 'DRAFT' },
  { id: 'open', label: 'Open', f: (d: DocRow) => ['ISSUED', 'PARTIALLY_PAID', 'ACCEPTED'].includes(d.status) },
  { id: 'overdue', label: 'Overdue', f: (d: DocRow) => d.status === 'OVERDUE' },
  { id: 'closed', label: 'Closed', f: (d: DocRow) => ['PAID', 'VOID', 'WRITTEN_OFF', 'EXPIRED', 'DECLINED'].includes(d.status) },
];

const toDocRow = (d: any): DocRow => ({
  id: d.id,
  type: d.doc_type,
  number: d.doc_number || 'Draft',
  customer: d.contact_name || d.contact_id || '—',
  sub: '',
  date: d.doc_date,
  due: d.due_date || '—',
  status: d.status,
  amount: money(d.grand_total),
  balance: money(d.grand_total) - money(d.amount_settled),
  ai: d.created_via === 'AI',
});

type Col = (key: string, align?: 'left' | 'right' | 'center') => any;

/** Hover-reactive table row. */
const DocumentRow: React.FC<{
  d: DocRow;
  index: number;
  showDue: boolean;
  col: Col;
  noContactLabel: string;
  onOpen: () => void;
  onDelete: () => void;
}> = ({ d, index, showDue, col, noContactLabel, onOpen, onDelete }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  const [delHover, setDelHover] = useState(false);

  // staggered entrance
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 380, delay: Math.min(index, 12) * 45, easing: Easing.out(Easing.cubic), useNativeDriver: !isWeb }).start();
  }, [a, index]);

  const isDraft = d.status === 'DRAFT';
  const isVoid = d.status === 'VOID' || d.status === 'WRITTEN_OFF';
  const known = !!d.customer && d.customer !== '—';
  const edge = isDraft ? colors.accent : d.status === 'OVERDUE' ? colors.danger : hover ? colors.accent : 'transparent';
  const [g1, g2] = avatarOf(d.customer || '—');
  const cellMono = { fontFamily: font.mono, fontSize: 13, color: colors.ink };

  return (
    <Animated.View style={{ opacity: a, transform: [{ translateY: a.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) }] }}>
      <Pressable
        onPress={onOpen}
        style={{
          flexDirection: 'row',
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
          backgroundColor: hover ? colors.paper2 : colors.card,
          ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }),
        }}
        {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
      >
        <View style={[col('num'), { borderLeftWidth: 3, borderLeftColor: edge, ...web({ transition: 'border-color .12s ease' }) }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '600', color: colors.ink, textDecorationLine: isVoid ? 'line-through' : 'none' }}>
              {isDraft ? 'Draft' : d.number}
            </Text>
            {isDraft && (
              <View style={{ borderWidth: 1, borderColor: colors.lineStrong, borderStyle: 'dashed', paddingHorizontal: 5, paddingVertical: 1, borderRadius: 4 }}>
                <Text style={{ fontSize: 9, fontWeight: '700', letterSpacing: 0.6, color: colors.ink3 }}>DRAFT</Text>
              </View>
            )}
            {d.ai && (
              <View style={{ width: 16, height: 16, borderRadius: 5, backgroundColor: colors.purpleSoft, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ fontSize: 9, color: colors.purple }}>✦</Text>
              </View>
            )}
          </View>
        </View>
        <View style={col('cust')}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            {known ? (
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: g2,
                  ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 2px 6px -2px ${g2}66` }),
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff', fontFamily: font.sansBold }}>{initials(d.customer)}</Text>
              </View>
            ) : (
              <View style={{ width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line }}>
                <SkIcon name="building" size={13} color={colors.ink4} strokeWidth={2} />
              </View>
            )}
            <View style={{ minWidth: 0, flex: 1 }}>
              <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontWeight: '500', fontSize: 13, color: known ? colors.ink : colors.ink4 }}>
                {known ? d.customer : noContactLabel}
              </Text>
              {!!d.sub && <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>{d.sub}</Text>}
            </View>
          </View>
        </View>
        <View style={col('date', 'center')}><Text style={[cellMono, { fontSize: 13, color: colors.ink2 }]}>{d.date}</Text></View>
        {showDue && <View style={col('due', 'center')}><Text style={[cellMono, { fontSize: 13, color: d.status === 'OVERDUE' ? colors.danger : colors.ink2 }]}>{d.due}</Text></View>}
        <View style={col('status', 'center')}><View style={{ alignSelf: 'center' }}><StatusPill status={d.status} /></View></View>
        <View style={col('amount', 'right')}><Text style={{ fontFamily: font.monoSemibold, fontSize: 14, fontWeight: '600', color: colors.ink, letterSpacing: -0.2, textDecorationLine: isVoid ? 'line-through' : 'none' }}>{fmtMoney(d.amount, cur)}</Text></View>
        <View style={col('bal', 'right')}><Text style={[cellMono, { color: d.balance !== 0 ? colors.ink2 : colors.ink4 }]}>{d.balance === 0 ? '—' : fmtMoney(d.balance, cur)}</Text></View>
        <View style={col('act')}>
        {/* Only a draft can be deleted — an issued document is immutable. */}
        {d.status === 'DRAFT' && (
        <Pressable
          onPress={(e: any) => { e?.stopPropagation?.(); onDelete(); }}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={{
            padding: 6,
            borderRadius: 7,
            backgroundColor: delHover ? colors.dangerSoft : 'transparent',
            ...web({ transition: 'background-color .12s ease' }),
          }}
          {...(isWeb ? { onMouseEnter: () => setDelHover(true), onMouseLeave: () => setDelHover(false) } : {})}
        >
          <SkIcon name="trash" size={15} color={delHover ? colors.danger : colors.ink4} strokeWidth={2} />
        </Pressable>
        )}
        </View>
      </Pressable>
    </Animated.View>
  );
};

/** Mobile card — replaces the cramped horizontal-scroll table on small screens
 *  with a stacked, tappable card per document. */
const DocumentCard: React.FC<{
  d: DocRow;
  index: number;
  showDue: boolean;
  noContactLabel: string;
  onOpen: () => void;
  onDelete: () => void;
}> = ({ d, index, showDue, noContactLabel, onOpen, onDelete }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [delHover, setDelHover] = useState(false);

  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 380, delay: Math.min(index, 12) * 45, easing: Easing.out(Easing.cubic), useNativeDriver: !isWeb }).start();
  }, [a, index]);

  const isDraft = d.status === 'DRAFT';
  const isVoid = d.status === 'VOID' || d.status === 'WRITTEN_OFF';
  const known = !!d.customer && d.customer !== '—';
  const edge = isDraft ? colors.accent : d.status === 'OVERDUE' ? colors.danger : 'transparent';
  const [g1, g2] = avatarOf(d.customer || '—');

  return (
    <Animated.View style={{ opacity: a, transform: [{ translateY: a.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) }] }}>
      <Pressable
        onPress={onOpen}
        style={{
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
          borderLeftWidth: 3,
          borderLeftColor: edge,
          paddingVertical: 14,
          paddingHorizontal: 16,
          backgroundColor: colors.card,
          ...web({ cursor: 'pointer' }),
        }}
      >
        {/* number + status */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            <Text numberOfLines={1} style={{ fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '600', color: colors.ink, textDecorationLine: isVoid ? 'line-through' : 'none' }}>
              {isDraft ? 'Draft' : d.number}
            </Text>
            {d.ai && (
              <View style={{ width: 16, height: 16, borderRadius: 5, backgroundColor: colors.purpleSoft, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ fontSize: 9, color: colors.purple }}>✦</Text>
              </View>
            )}
          </View>
          <StatusPill status={d.status} />
        </View>

        {/* customer + delete */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          {known ? (
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: g2,
                ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 2px 6px -2px ${g2}66` }),
              }}
            >
              <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff', fontFamily: font.sansBold }}>{initials(d.customer)}</Text>
            </View>
          ) : (
            <View style={{ width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line }}>
              <SkIcon name="building" size={14} color={colors.ink4} strokeWidth={2} />
            </View>
          )}
          <Text numberOfLines={1} style={{ flex: 1, fontFamily: font.sansMedium, fontWeight: '500', fontSize: 14, color: known ? colors.ink : colors.ink4 }}>
            {known ? d.customer : noContactLabel}
          </Text>
          {d.status === 'DRAFT' && (
          <Pressable
            onPress={(e: any) => { e?.stopPropagation?.(); onDelete(); }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={{ padding: 7, borderRadius: 8, backgroundColor: delHover ? colors.dangerSoft : colors.paper2 }}
            {...(isWeb ? { onMouseEnter: () => setDelHover(true), onMouseLeave: () => setDelHover(false) } : {})}
          >
            <SkIcon name="trash" size={15} color={delHover ? colors.danger : colors.ink4} strokeWidth={2} />
          </Pressable>
          )}
        </View>

        {/* footer: date · amount */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <View>
            <Text style={{ fontFamily: font.sans, fontSize: 9.5, color: colors.ink4, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.5 }}>{showDue ? 'Date · Due' : 'Date'}</Text>
            <Text style={{ fontFamily: font.mono, fontSize: 12.5, color: colors.ink2, marginTop: 2 }}>
              {d.date}{showDue ? `  ·  ${d.due}` : ''}
            </Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 9.5, color: colors.ink4, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.5 }}>Amount</Text>
            <Text style={{ fontFamily: font.monoSemibold, fontSize: 16, fontWeight: '600', color: colors.ink, letterSpacing: -0.3, marginTop: 2, textDecorationLine: isVoid ? 'line-through' : 'none' }}>
              {fmtMoney(d.amount, cur)}
            </Text>
            {d.balance !== 0 && d.balance !== d.amount && (
              <Text style={{ fontFamily: font.mono, fontSize: 11, color: colors.ink4, marginTop: 1 }}>Bal {fmtMoney(d.balance, cur)}</Text>
            )}
          </View>
        </View>
      </Pressable>
    </Animated.View>
  );
};

/** Interactive summary stat card — hover-lifts and filters the list on press. */
const StatCard: React.FC<{ label: string; value: string; icon: string; ink: string; soft: string; line: string; active: boolean; onPress: () => void }> = ({ label, value, icon, ink, soft, line, active, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1,
        minWidth: 150,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 13,
        paddingHorizontal: 15,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: active ? line : hover ? colors.lineStrong : colors.line,
        backgroundColor: active ? soft : colors.card,
        ...theme.shadow(hover ? 'md' : 'sm'),
        ...web({ transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease', transform: hover ? 'translateY(-2px)' : 'translateY(0)', cursor: 'pointer', ...(active ? { backgroundImage: `linear-gradient(145deg, ${soft}, ${colors.card})` } : {}) }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: 38, height: 38, borderRadius: 11, backgroundColor: soft, borderWidth: 1, borderColor: line, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${soft}, ${colors.card})`, boxShadow: `0 4px 10px -3px ${ink}40` }) }}>
        <SkIcon name={icon} size={17} color={ink} strokeWidth={2.1} />
      </View>
      <View style={{ minWidth: 0 }}>
        <Text style={{ fontFamily: font.sansMedium, fontSize: 11.5, fontWeight: '500', color: colors.ink3 }}>{label}</Text>
        <Text numberOfLines={1} style={{ fontFamily: font.monoSemibold, fontSize: 18, fontWeight: '600', color: colors.ink, letterSpacing: -0.4, marginTop: 1 }}>{value}</Text>
      </View>
    </Pressable>
  );
};

const DocumentList: React.FC<Props> = ({ docType, newLabel, onOpenDoc, onNew }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const { isWide, isMobile } = useResponsive();
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newHover, setNewHover] = useState(false);

  // Delete flow
  const [confirmDoc, setConfirmDoc] = useState<DocRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [deletedLabel, setDeletedLabel] = useState('');

  const fetchDocs = useCallback(() => {
    return listDocuments({ type: docType })
      .then((list) => setDocs((Array.isArray(list) ? list : []).map(toDocRow)))
      .catch(() => setDocs([]));
  }, [docType]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setDocs([]);
    setTab('all');
    setQuery('');
    listDocuments({ type: docType })
      .then((list) => { if (alive) setDocs((Array.isArray(list) ? list : []).map(toDocRow)); })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [docType]);

  const vendorSide = docType === 'BILL' || docType === 'VENDOR_CREDIT' || docType === 'EXPENSE';
  const showDue = docType === 'INVOICE' || docType === 'BILL';

  const counts = useMemo(() => TABS.map((t) => ({ ...t, count: docs.filter(t.f).length })), [docs]);
  const rows = useMemo(() => {
    const def = TABS.find((t) => t.id === tab) || TABS[0];
    let r = docs.filter(def.f);
    const q = query.trim().toLowerCase();
    if (q) r = r.filter((d) => (d.customer + ' ' + d.number + ' ' + d.sub).toLowerCase().includes(q));
    return r;
  }, [docs, tab, query]);

  const open = docs.filter((d) => ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(d.status));
  const openSum = open.reduce((s, d) => s + d.balance, 0);
  // Drafts and voids are not business done; counting them inflated the total.
  const totalValue = docs.filter((d) => d.status !== 'DRAFT' && d.status !== 'VOID').reduce((s, d) => s + Math.abs(d.amount), 0);
  const overdueCount = docs.filter((d) => d.status === 'OVERDUE').length;

  const onConfirmDelete = async () => {
    if (!confirmDoc || deleting) return;
    setDeleting(true);
    try {
      await deleteDocument(confirmDoc.id);
      setDeletedLabel(confirmDoc.status === 'DRAFT' ? 'The draft' : confirmDoc.number);
      setConfirmDoc(null);
      setDeleting(false);
      setSuccessOpen(true);
      fetchDocs();
    } catch {
      // Error toast (e.g. "issued documents can't be deleted") shown by the service.
      setDeleting(false);
      setConfirmDoc(null);
    }
  };

  // Column config — each has a fixed mobile width (`w`, used when the table
  // scrolls horizontally) and a desktop flex weight (`flex`, so the row fills
  // the width evenly instead of one greedy column leaving a big gap). The
  // action column stays a fixed icon width on every breakpoint.
  const colCfg: Record<string, { w: number; flex: number; fixed?: boolean }> = {
    num: { w: 160, flex: 1.5 },
    cust: { w: 240, flex: 2.8 },
    date: { w: 120, flex: 1.0 },
    due: { w: 110, flex: 1.0 },
    status: { w: 120, flex: 1.0 },
    amount: { w: 120, flex: 1.0 },
    bal: { w: 120, flex: 1.0 },
    act: { w: 84, flex: 0, fixed: true },
  };
  const col: Col = (key, align = 'left') => {
    const c = colCfg[key];
    const center = key === 'act' || align === 'center';
    const base = {
      paddingHorizontal: 16,
      paddingVertical: 13,
      justifyContent: 'center' as const,
      alignItems: center ? ('center' as const) : align === 'right' ? ('flex-end' as const) : ('flex-start' as const),
    };
    if (c.fixed || isMobile) return { ...base, width: c.w };
    return { ...base, flex: c.flex };
  };
  const th = { fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600' as const, color: colors.ink4, letterSpacing: 0.6, textTransform: 'uppercase' as const };

  // Proper, document-type-aware header for the number column.
  const NOUN: Record<string, string> = {
    INVOICE: 'Invoice', QUOTE: 'Quote', CREDIT_NOTE: 'Credit Note', BILL: 'Bill', VENDOR_CREDIT: 'Vendor Credit', EXPENSE: 'Expense',
  };
  const numHeader = `${NOUN[docType] || 'Document'} No.`;

  // Premium, encouraging empty states — tailored to the active tab so a missing
  // row reads as good news ("nothing overdue") rather than a dead end.
  const noun = (NOUN[docType] || 'document').toLowerCase();
  const plural = `${noun}s`;
  const emptyState: { tone: 'success' | 'info' | 'accent' | 'neutral'; icon: string; title: string; sub: string; cta: boolean } =
    query.trim()
      ? { tone: 'neutral', icon: 'search', title: 'No matches found', sub: `No ${plural} match “${query.trim()}”. Try a different word or clear the search.`, cta: false }
      : tab === 'draft'
        ? { tone: 'success', icon: 'checkCircle', title: 'All caught up', sub: `No drafts waiting — every ${noun} has been finalized. Nice and tidy.`, cta: false }
        : tab === 'open'
          ? { tone: 'success', icon: 'checkCircle', title: 'Nothing open right now', sub: `No ${plural} are awaiting a response — you’re fully on top of it.`, cta: false }
          : tab === 'overdue'
            ? { tone: 'success', icon: 'checkCircle', title: 'Nothing overdue 🎉', sub: `Great work — not a single ${noun} has slipped past its date.`, cta: false }
            : tab === 'closed'
              ? { tone: 'info', icon: 'fileText', title: `No closed ${plural} yet`, sub: `Completed and archived ${plural} will appear here as you wrap them up.`, cta: false }
              : { tone: 'accent', icon: 'plus', title: `Create your first ${noun}`, sub: `You haven’t added any ${plural} yet — let’s get the first one out the door.`, cta: true };
  const toneColors: Record<string, { soft: string; ink: string; line: string }> = {
    success: { soft: colors.successSoft, ink: colors.success, line: colors.successLine },
    info: { soft: colors.infoSoft, ink: colors.info, line: colors.infoLine },
    accent: { soft: colors.accentSoft, ink: colors.accent, line: colors.accentLine },
    neutral: { soft: colors.paper2, ink: colors.ink4, line: colors.line },
  };
  const tc = toneColors[emptyState.tone];

  const footChip = (label: string, accent = false) => (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 5,
        paddingHorizontal: 11,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: accent ? colors.accentLine : colors.line,
        backgroundColor: accent ? colors.accentSoft : colors.card,
      }}
    >
      {accent && <SkIcon name="rupee" size={12} color={colors.accentInk} strokeWidth={2.2} />}
      <Text style={{ fontFamily: accent ? font.sansSemibold : font.sansMedium, fontSize: 11.5, fontWeight: accent ? '600' : '500', color: accent ? colors.accentInk : colors.ink3 }}>{label}</Text>
    </View>
  );

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* Summary stat strip — derived from the loaded list; tap to filter */}
      {!loading && docs.length > 0 && (
        <View style={{ flexDirection: 'row', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { label: 'Total value', value: fmtMoney(totalValue, cur), icon: 'fileText', ink: colors.accent, soft: colors.accentSoft, line: colors.accentLine, tab: 'all' },
            { label: docType === 'QUOTE' ? 'Open value' : 'Outstanding', value: fmtMoney(openSum, cur), icon: 'rupee', ink: colors.warn, soft: colors.warnSoft, line: colors.warnLine, tab: 'open' },
            { label: 'Overdue', value: String(overdueCount), icon: 'clock', ink: colors.danger, soft: colors.dangerSoft, line: colors.dangerLine, tab: 'overdue' },
          ].map((s) => (
            <StatCard key={s.label} label={s.label} value={s.value} icon={s.icon} ink={s.ink} soft={s.soft} line={s.line} active={tab === s.tab} onPress={() => setTab(s.tab)} />
          ))}
        </View>
      )}

      {/* Search + New (row 1, above the tabs) */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <View style={{ width: 300, maxWidth: '55%', position: 'relative' }}>
          <View style={{ position: 'absolute', left: 11, top: 0, bottom: 0, justifyContent: 'center', zIndex: 1 }}>
            <SkIcon name="search" size={14} color={colors.ink4} strokeWidth={2} />
          </View>
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search this list…"
            placeholderTextColor={colors.ink4}
            style={{ height: 36, paddingLeft: 34, paddingRight: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: 9, fontSize: 12.5, color: colors.ink, fontFamily: font.sans, ...theme.shadow('sm'), ...web({ outlineStyle: 'none' }) }}
          />
        </View>
        <View style={{ flex: 1 }} />
        <Pressable
          onPress={onNew}
          style={{
            height: 40,
            paddingHorizontal: 16,
            borderRadius: 10,
            backgroundColor: CTA_A,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 7,
            ...web({
              backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`,
              boxShadow: newHover ? `0 10px 22px -4px ${CTA_B}aa` : `0 5px 12px -3px ${CTA_B}77`,
              transform: newHover ? 'translateY(-1px)' : 'translateY(0)',
              transition: 'transform .14s ease, box-shadow .14s ease',
              cursor: 'pointer',
            }),
          }}
          {...(isWeb ? { onMouseEnter: () => setNewHover(true), onMouseLeave: () => setNewHover(false) } : {})}
        >
          <SkIcon name="plus" size={15} color="#fff" strokeWidth={2.4} />
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>{newLabel}</Text>
        </Pressable>
      </View>

      {/* Status tabs (row 2) */}
      <View style={{ flexDirection: 'row', marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', gap: 3, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
          {counts.map((t) => {
            const active = t.id === tab;
            return (
              <Pressable
                key={t.id}
                onPress={() => setTab(t.id)}
                style={{
                  height: 30,
                  paddingHorizontal: 13,
                  borderRadius: 7,
                  justifyContent: 'center',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 7,
                  backgroundColor: active ? colors.card : 'transparent',
                  ...(active ? web({ boxShadow: '0 1px 3px rgba(16,24,40,.10)' }) : {}),
                  ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }),
                }}
              >
                <Text style={{ fontFamily: active ? font.sansSemibold : font.sansMedium, fontSize: 12.5, fontWeight: active ? '600' : '500', color: active ? colors.ink : colors.ink3 }}>{t.label}</Text>
                <View style={{ minWidth: 18, height: 17, paddingHorizontal: 5, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.accentSoft : colors.paper3 }}>
                  <Text style={{ fontFamily: font.monoMedium, fontSize: 10, color: active ? colors.accentInk : colors.ink4 }}>{t.count}</Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Table card (desktop) / stacked cards (mobile) */}
      <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden', ...theme.shadow('sm') }}>
        {/* Titled card header — unifies the look with Customers / Statements */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 14, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line }}>
          <View style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 3px 8px -3px ${colors.accent}55` }) }}>
            <SkIcon name={DOC_ICON[docType] || 'fileText'} size={16} color={colors.accentInk} strokeWidth={2} />
          </View>
          <Text style={{ fontFamily: font.sansBold, fontSize: 14.5, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>{`${NOUN[docType] || 'Document'}s`}</Text>
          <View style={{ flex: 1 }} />
          {!loading && (
            <View style={{ paddingHorizontal: 9, height: 22, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 11, color: colors.ink3 }}>{docs.length} total</Text>
            </View>
          )}
        </View>
        {loading ? (
          <View style={{ padding: 48, alignItems: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3 }}>Loading…</Text>
          </View>
        ) : rows.length === 0 ? (
          <View style={{ paddingVertical: 64, paddingHorizontal: 24, alignItems: 'center' }}>
            {/* Layered medallion — soft halo ring + gradient inner tile */}
            <View
              style={{
                width: 96,
                height: 96,
                borderRadius: 30,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
                backgroundColor: tc.soft,
                borderWidth: 1,
                borderColor: tc.line,
                ...web({ backgroundImage: `radial-gradient(circle at 50% 32%, ${tc.soft}, ${colors.card})`, boxShadow: `0 14px 38px -10px ${tc.ink}4d` }),
              }}
            >
              <View
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: 19,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: colors.card,
                  borderWidth: 1,
                  borderColor: tc.line,
                  ...web({ backgroundImage: `linear-gradient(145deg, ${tc.soft}, ${colors.card})`, boxShadow: `0 6px 16px -6px ${tc.ink}66` }),
                }}
              >
                <SkIcon name={emptyState.icon} size={26} color={tc.ink} strokeWidth={2} />
              </View>
            </View>
            <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 18, color: colors.ink, letterSpacing: -0.3, textAlign: 'center' }}>{emptyState.title}</Text>
            <Text style={{ fontFamily: font.sans, fontSize: 13.5, color: colors.ink3, marginTop: 6, textAlign: 'center', maxWidth: 400, lineHeight: 20 }}>{emptyState.sub}</Text>
            {emptyState.cta && (
              <Pressable
                onPress={onNew}
                style={{
                  marginTop: 20,
                  height: 38,
                  paddingHorizontal: 16,
                  borderRadius: 10,
                  backgroundColor: CTA_A,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 7,
                  ...web({ backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`, boxShadow: `0 6px 14px -4px ${CTA_B}88`, cursor: 'pointer' }),
                }}
              >
                <SkIcon name="plus" size={15} color="#fff" strokeWidth={2.4} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>{newLabel}</Text>
              </Pressable>
            )}
          </View>
        ) : isMobile ? (
          // Stacked cards on mobile — no horizontal scroll, nothing cut off.
          rows.map((d, i) => (
            <DocumentCard key={d.id} d={d} index={i} showDue={showDue} noContactLabel={vendorSide ? 'No vendor' : 'No customer'} onOpen={() => onOpenDoc(d)} onDelete={() => setConfirmDoc(d)} />
          ))
        ) : (
          <View style={{ width: '100%' }}>
            {/* header */}
            <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderBottomWidth: 1, borderBottomColor: colors.line, ...web({ backgroundImage: `linear-gradient(180deg, ${colors.paper2}, ${colors.paper})` }) }}>
              <View style={col('num')}><Text style={th}>{numHeader}</Text></View>
              <View style={col('cust')}><Text style={th}>{vendorSide ? 'Vendor' : 'Customer'}</Text></View>
              <View style={col('date', 'center')}><Text style={th}>Date</Text></View>
              {showDue && <View style={col('due', 'center')}><Text style={th}>Due</Text></View>}
              <View style={col('status', 'center')}><Text style={th}>Status</Text></View>
              <View style={col('amount', 'right')}><Text style={th}>Amount</Text></View>
              <View style={col('bal', 'right')}><Text style={th}>Balance</Text></View>
              <View style={col('act')}><Text style={th}>Action</Text></View>
            </View>
            {rows.map((d, i) => (
              <DocumentRow
                key={d.id}
                d={d}
                index={i}
                showDue={showDue}
                col={col}
                noContactLabel={vendorSide ? 'No vendor' : 'No customer'}
                onOpen={() => onOpenDoc(d)}
                onDelete={() => setConfirmDoc(d)}
              />
            ))}
          </View>
        )}

        {/* Card footer — summary chips, inside the card */}
        {!loading && (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, paddingHorizontal: 16, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexWrap: 'wrap' }}>
            {footChip(`${rows.length} shown`)}
            {footChip(`${open.length} open`)}
            {footChip(`${fmtMoney(openSum, cur)} ${docType === 'QUOTE' ? 'open' : 'outstanding'}`, true)}
          </View>
        )}
      </View>

      {/* Delete confirm */}
      <ConfirmDialog
        visible={!!confirmDoc}
        title="Delete this document?"
        message={
          confirmDoc
            ? `${confirmDoc.status === 'DRAFT' ? 'This draft' : confirmDoc.number} will be permanently deleted and can't be undone. (Only drafts can be deleted — issued documents must be voided.)`
            : ''
        }
        confirmText="Delete"
        busy={deleting}
        onConfirm={onConfirmDelete}
        onCancel={() => (deleting ? null : setConfirmDoc(null))}
      />

      {/* Delete success */}
      <SuccessDialog
        visible={successOpen}
        title="Deleted"
        message={`${deletedLabel || 'The document'} was deleted.`}
        primaryAction={{ label: 'Done', onPress: () => setSuccessOpen(false) }}
        onClose={() => setSuccessOpen(false)}
      />
    </View>
  );
};

export default DocumentList;
